import { LoaderFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthContextType } from '../../../../hooks/useAuth';
import { client } from '../../../../gql/client';
import { GET_SURVEILLANCE_ROUTES } from '../../../../gql/queries/surveillanceRoute';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { GET_ROADS } from '../../../../gql/queries/geoPositions/roads';
import { selectOrdersForSurveillanceRoutes } from '../../../../gql/selectors/surveillanceRoute';
import { store } from '../../../../state/configureStore';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { tableIds, takeBase } from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

export const LoaderSearchAndCreateHistoricalRoute: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
    const cursorSR = getCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES)(store.getState());
    const dispatch = store.dispatch;

    const roleActive = userData?.user?.user?.rolActive;

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido (React Router cancel, hot reload, throw mid-fetch).
    // Sin esto, si el run anterior abrió el flag y no llegó al cierre, este
    // run cae en HIT path y nunca lo cierra → tabla bloqueada permanentemente.
    dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES, false));

    if (userData?.user) {
      // Cache-read-first pattern
      const readSurveillanceRoutes = client.readQuery({
        query: GET_SURVEILLANCE_ROUTES,
        variables: {
          input: {
            take: cursorSR.take,
            cursor: cursorSR.cursor,
            sortField: cursorSR.sortField,
            orderBy: cursorSR.orderBy,
            concessionId: cursorSR.concessionId,
            name: cursorSR.routeName,
            roadId: cursorSR.roadId,
            state: cursorSR.state,
          states: cursorSR.states,
            fromDate: cursorSR.fromDate,
            toDate: cursorSR.toDate,
            teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
            capitolId: cursorSR.capitolId,
            subCapitolId: cursorSR.subCapitolId,
            predefinedOrderId: cursorSR.predefinedOrderId,
            indexExpedition: cursorSR.indexExpedition,
            indexType: cursorSR.indexType,
            pkInitKm: cursorSR.pkInitKm,
            pkInitMeter: cursorSR.pkInitMeter,
            pkEndKm: cursorSR.pkEndKm,
            pkEndMeter: cursorSR.pkEndMeter,
            description: cursorSR.description,
            // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
            onlyFlagged: cursorSR.onlyFlagged,
          },
        },
      });

      const pageInfo = readSurveillanceRoutes?.surveillanceRoutes?.pageInfo;
      const totalCount = readSurveillanceRoutes?.surveillanceRoutes?.totalCount;
      const isCacheEmpty = JSON.stringify(readSurveillanceRoutes) === '{}';

      const surveillanceRoutes =
        readSurveillanceRoutes &&
        !isCacheEmpty &&
        readSurveillanceRoutes?.surveillanceRoutes?.surveillanceRoutes;

      // Fetch all filter data sources (V4 iter7: + roads).
      const [
        { data: { capitols } },
        { data: { concessions } },
        { data: { subCapitols } },
        { data: { classifications } },
        { data: { teams } },
        { data: { predefinedOrders } },
        { data: { roads } },
        { data: { users } },
      ] = await Promise.all([
        client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_CLASSIFICATIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_ROADS, fetchPolicy: 'cache-first' }),
        // 2026-07-12: usuarios para el dropdown Operarios (showOperatorsFilter).
        client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      ]);

      const filtersDataOptions = {
        capitols,
        concessions,
        subCapitols,
        classifications,
        teams,
        predefinedOrders,
        roads,
        users,
      };

      // Patrón canónico entidad doble: cargar las Orders asociadas a cada SR.
      const ordersBySurveillanceRouteId = await selectOrdersForSurveillanceRoutes(
        surveillanceRoutes || [],
      );

      const combinedData = {
        surveillanceRoutes,
        filtersDataOptions,
        ordersBySurveillanceRouteId,
        totalCount,
        pageInfo,
        loading: false,
        roleActive,
      };

      if (!readSurveillanceRoutes || isCacheEmpty) {
        dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES, true));
        try {
        let data;
        if (isCacheEmpty) {
          data = await client.query({
            query: GET_SURVEILLANCE_ROUTES,
            variables: {
              input: {
                take: firstPageTake(cursorSR.take, takeBase),
                cursor: '',
                sortField: cursorSR.sortField,
                orderBy: cursorSR.orderBy ?? 'desc',
                concessionId: cursorSR.concessionId,
                name: cursorSR.routeName,
                roadId: cursorSR.roadId,
                state: cursorSR.state,
          states: cursorSR.states,
                fromDate: cursorSR.fromDate,
                toDate: cursorSR.toDate,
                teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
                capitolId: cursorSR.capitolId,
                subCapitolId: cursorSR.subCapitolId,
                    predefinedOrderId: cursorSR.predefinedOrderId,
                indexExpedition: cursorSR.indexExpedition,
                indexType: cursorSR.indexType,
                pkInitKm: cursorSR.pkInitKm,
                pkInitMeter: cursorSR.pkInitMeter,
                pkEndKm: cursorSR.pkEndKm,
                pkEndMeter: cursorSR.pkEndMeter,
                description: cursorSR.description,
                onlyFlagged: cursorSR.onlyFlagged,
                  },
            },
            fetchPolicy: 'cache-first',
          });

          const setCursorData = {
            take: firstPageTake(cursorSR.take, takeBase),
            cursor: '',
            // #74: sortField/orderBy se OMITEN (el merge del reducer los preserva).
            concessionId: cursorSR.concessionId,
            // Campo del CURSOR Redux = `routeName` (la var de la query es `name`).
            routeName: cursorSR.routeName,
            roadId: cursorSR.roadId,
            state: cursorSR.state,
          states: cursorSR.states,
            fromDate: cursorSR.fromDate,
            toDate: cursorSR.toDate,
            teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
            capitolId: cursorSR.capitolId,
            subCapitolId: cursorSR.subCapitolId,
            predefinedOrderId: cursorSR.predefinedOrderId,
            indexExpedition: cursorSR.indexExpedition,
            indexType: cursorSR.indexType,
            pkInitKm: cursorSR.pkInitKm,
            pkInitMeter: cursorSR.pkInitMeter,
            pkEndKm: cursorSR.pkEndKm,
            pkEndMeter: cursorSR.pkEndMeter,
            description: cursorSR.description,
            onlyFlagged: cursorSR.onlyFlagged,
          };

          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES, setCursorData));
        } else {
          data = await client.query({
            query: GET_SURVEILLANCE_ROUTES,
            variables: {
              input: {
                take: cursorSR.take,
                cursor: cursorSR.cursor,
                sortField: cursorSR.sortField,
                orderBy: cursorSR.orderBy,
                concessionId: cursorSR.concessionId,
                name: cursorSR.routeName,
                roadId: cursorSR.roadId,
                state: cursorSR.state,
          states: cursorSR.states,
                fromDate: cursorSR.fromDate,
                toDate: cursorSR.toDate,
                teamId: cursorSR.teamId,
          teamIds: cursorSR.teamIds,
          operatorIds: cursorSR.operatorIds,
          userIds: cursorSR.userIds,
                capitolId: cursorSR.capitolId,
                subCapitolId: cursorSR.subCapitolId,
                    predefinedOrderId: cursorSR.predefinedOrderId,
                indexExpedition: cursorSR.indexExpedition,
                indexType: cursorSR.indexType,
                pkInitKm: cursorSR.pkInitKm,
                pkInitMeter: cursorSR.pkInitMeter,
                pkEndKm: cursorSR.pkEndKm,
                pkEndMeter: cursorSR.pkEndMeter,
                description: cursorSR.description,
                onlyFlagged: cursorSR.onlyFlagged,
                  },
            },
            fetchPolicy: 'cache-first',
          });
        }

        const loading = data.loading;
        const pageInfo = data.data.surveillanceRoutes.pageInfo;
        const totalCount = data.data.surveillanceRoutes.totalCount;
        const surveillanceRoutes = data.data.surveillanceRoutes.surveillanceRoutes;

        const ordersBySurveillanceRouteId =
          await selectOrdersForSurveillanceRoutes(surveillanceRoutes || []);

        const combinedData = {
          surveillanceRoutes,
          filtersDataOptions,
          ordersBySurveillanceRouteId,
          totalCount,
          pageInfo,
          loading,
          roleActive,
        };

        return combinedData;
        } finally {
          // FIX (loader-loading-flag-leak): cierra el flag aunque el bloque MISS
          // lance una excepción a mitad (selector throw, query error). Antes,
          // si se lanzaba entre dispatch(true) y dispatch(false), el flag
          // quedaba fugado y la tabla bloqueada permanentemente.
          dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES, false));
        }
      }

      return combinedData;
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('Loader error (SR query may have failed, filter data preserved):', err);
    // FIX (loader-loading-flag-leak): defensa en profundidad — si algo entre
    // el dispatch(false) inicial y el catch lanzó (p.ej. error en bootstrap
    // Promise.all antes del MISS branch), aseguramos que el flag esté cerrado.
    store.dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES, false));

    let filtersDataOptionsFallback = {
      capitols: [] as any[],
      concessions: [] as any[],
      subCapitols: [] as any[],
      classifications: [] as any[],
      teams: [] as any[],
      predefinedOrders: [] as any[],
      roads: [] as any[],
      users: [] as any[],
    };
    try {
      const [
        { data: { capitols } },
        { data: { concessions } },
        { data: { subCapitols } },
        { data: { classifications } },
        { data: { teams } },
        { data: { predefinedOrders } },
        { data: { roads } },
        { data: { users } },
      ] = await Promise.all([
        client.query({ query: FETCH_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_SUB_CAPITOLS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_CLASSIFICATIONS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_PREDEFINED_ORDERS, fetchPolicy: 'cache-first' }),
        client.query({ query: GET_ROADS, fetchPolicy: 'cache-first' }),
        client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
      ]);
      filtersDataOptionsFallback = { capitols, concessions, subCapitols, classifications, teams, predefinedOrders, roads, users };
    } catch (_) { /* filter queries also failed */ }

    return {
        surveillanceRoutes: [],
        filtersDataOptions: filtersDataOptionsFallback,
        totalCount: 0,
        // V4 PR6 cleanup (2026-05-07): sin `lastCursor` legacy.
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        loading: false,
        roleActive: userData?.user?.user?.rolActive,
      };
  }
};

  return { data: load() };
};

export default LoaderSearchAndCreateHistoricalRoute;
